import { Injectable, inject, signal, computed } from '@angular/core';
import { DatabaseService } from '../../offline/database.service';

export interface AppNotification {
  id: number;
  title: string;
  body: string;
  type: string;
  workOrderId: string | null;
  dataJson: string | null;
  read: boolean;
  receivedAt: string;
}

interface NotificationRow {
  id: number;
  title: string;
  body: string;
  type: string;
  workOrderId: string | null;
  dataJson: string | null;
  read: number;
  receivedAt: string;
}

/**
 * NotificationsService — Manages in-app notification persistence and state.
 *
 * Stores notifications in local SQLite for offline access.
 * Exposes reactive signals for unread count (badge) and notification list.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly db = inject(DatabaseService);

  private readonly _notifications = signal<AppNotification[]>([]);
  private readonly _unreadCount = signal(0);

  /** All notifications, most recent first */
  readonly notifications = this._notifications.asReadonly();

  /** Number of unread notifications (for badge) */
  readonly unreadCount = this._unreadCount.asReadonly();

  /** Whether there are unread notifications */
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  /** Load notifications from SQLite and refresh signals */
  async loadNotifications(): Promise<void> {
    try {
      const rows = await this.db.query<NotificationRow>(
        'SELECT * FROM notifications ORDER BY receivedAt DESC LIMIT 50',
      );

      if (rows.length > 0) {
        const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
        const recent = rows
          .filter((row) => new Date(row.receivedAt).getTime() > twoDaysAgo)
          .map((row) => ({ ...row, read: row.read === 1 }));

        this._notifications.set(recent);
        this._unreadCount.set(recent.filter((n) => !n.read).length);
      }
    } catch {
      // DB not ready yet — keep in-memory state as-is
    }
  }

  /** Add a new notification (called when a push is received) */
  async addNotification(notification: {
    title: string;
    body: string;
    type: string;
    workOrderId?: string;
    data?: Record<string, string>;
  }): Promise<void> {
    const now = new Date().toISOString();

    // Always add to in-memory list immediately (regardless of DB state)
    const newNotification: AppNotification = {
      id: Date.now(),
      title: notification.title,
      body: notification.body,
      type: notification.type,
      workOrderId: notification.workOrderId ?? null,
      dataJson: notification.data ? JSON.stringify(notification.data) : null,
      read: false,
      receivedAt: now,
    };

    this._notifications.update((list) => [newNotification, ...list].filter(
      (n) => Date.now() - new Date(n.receivedAt).getTime() < 2 * 24 * 60 * 60 * 1000,
    ));
    this._unreadCount.update((count) => count + 1);

    // Also persist to SQLite (best-effort, retries)
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await this.db.execute(
          `CREATE TABLE IF NOT EXISTS notifications (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            title TEXT NOT NULL,
            body TEXT NOT NULL,
            type TEXT NOT NULL,
            workOrderId TEXT,
            dataJson TEXT,
            read INTEGER DEFAULT 0,
            receivedAt TEXT NOT NULL
          )`,
        );

        await this.db.run(
          `INSERT INTO notifications (title, body, type, workOrderId, dataJson, read, receivedAt)
           VALUES (?, ?, ?, ?, ?, 0, ?)`,
          [
            notification.title,
            notification.body,
            notification.type,
            notification.workOrderId ?? '',
            notification.data ? JSON.stringify(notification.data) : '',
            now,
          ],
        );
        return;
      } catch {
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }
  }

  /** Mark a single notification as read */
  async markAsRead(id: number): Promise<void> {
    // Update in-memory immediately
    this._notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    this._unreadCount.update((count) => Math.max(0, count - 1));

    // Persist to DB (best-effort)
    try {
      await this.db.run('UPDATE notifications SET read = 1 WHERE id = ?', [id]);
    } catch { /* DB may not be ready */ }
  }

  /** Mark all notifications as read */
  async markAllAsRead(): Promise<void> {
    // Update in-memory immediately
    this._notifications.update((list) =>
      list.map((n) => ({ ...n, read: true })),
    );
    this._unreadCount.set(0);

    // Persist to DB (best-effort)
    try {
      await this.db.run('UPDATE notifications SET read = 1 WHERE read = 0');
    } catch { /* DB may not be ready */ }
  }

  private async refreshUnreadCount(): Promise<void> {
    const result = await this.db.query<{ count: number }>(
      'SELECT COUNT(*) as count FROM notifications WHERE read = 0',
    );
    this._unreadCount.set(result[0]?.count ?? 0);
  }
}
